import { Modal as MUIModal, type ModalProps } from '@mui/material';
import clsx from 'clsx';

import { PrimaryButton } from '../Buttons/PrimaryButton';
import { Text } from '../Typography/Text';
import styles from './Modal.module.scss';

export interface IModalAction {
  text: string;
  color:
    | 'initial'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'textPrimary'
    | 'textSecondary'
    | 'error';
  onPress: () => void;
  disabled?: boolean;
}

export interface IModalProps extends Omit<ModalProps, 'children'> {
  open: boolean;
  overlineText?: string;
  setOpen: (setOpen: boolean) => void;
  titleText?: string;
  /** Only used if `renderContent` is undefined */
  messageText?: string;
  /** Overrides use of `messageText` */
  renderContent?: () => JSX.Element;
  actions?: IModalAction[];
}

export const Modal = ({
  open,
  overlineText,
  setOpen,
  titleText,
  messageText,
  renderContent,
  actions,
  ...restModalProps
}: IModalProps) => {
  const renderBody = () => {
    return (
      <>
        {overlineText && (
          <Text className={styles.overline} variant="overline">
            {overlineText}
          </Text>
        )}
        {titleText && (
          <Text className={styles.title} variant="h1">
            {titleText}
          </Text>
        )}
        {renderContent ? (
          renderContent()
        ) : (
          <>
            {messageText && (
              <Text className={styles.message} variant="body2">
                {messageText}
              </Text>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <MUIModal className={styles.modal} open={open} {...restModalProps}>
      <div className={styles.modalContent}>
        {renderBody()}
        {actions && actions.length > 0 && (
          <div
            className={clsx(styles.actions, {
              [styles.singleAction]: actions.length === 1,
            })}
          >
            {actions.map((action, i) => (
              <PrimaryButton
                disabled={action.disabled}
                fullWidth={false}
                key={i}
                onClick={action.onPress}
                variant="text"
              >
                <Text className={styles.btnText} color={action.color}>
                  {action.text}
                </Text>
              </PrimaryButton>
            ))}
          </div>
        )}
      </div>
    </MUIModal>
  );
};
